import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { useContent } from "../lib/content";
import { useCurrency } from "../lib/currency";
import { brandFor, BRANDS, darkLogoFor } from "../lib/brand";
import Navbar from "../components/Navbar";
import { Search, Package, ChevronLeft, ChevronRight, X, ShoppingCart, Flame } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 30;

export default function Catalog() {
  const { user } = useAuth();
  const { add } = useCart();
  const { t } = useContent();
  useCurrency(); // subscribe so prices re-render on currency switch
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [qDebounced, setQDebounced] = useState(params.get("q") || "");
  const [brand, setBrand] = useState(params.get("brand") || "");
  const [brandGroup, setBrandGroup] = useState(params.get("brand_group") || ""); // "" | "vag" | "mercedes" | "bmw"
  const [promoOnly, setPromoOnly] = useState(params.get("promo") === "true");
  const [category, setCategory] = useState(params.get("category") || "");
  const [promoCategories, setPromoCategories] = useState([]); // categories that contain >=1 active promo
  const [promoGroups, setPromoGroups] = useState([]); // [{label, count}] — Aktionsgruppen
  const [promoGroup, setPromoGroup] = useState(params.get("promo_label") || ""); // selected campaign filter
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => { setPage(0); }, [qDebounced, brand, brandGroup, promoOnly, category, promoGroup]);

  useEffect(() => {
    api.get("/price-groups").then((r) => setGroups(r.data));
  }, []);

  // Fetch the list of promo categories whenever the user enters / leaves
  // the "Akciók" view. Used to render per-category sub-tabs in the sidebar.
  useEffect(() => {
    if (!promoOnly) {
      setPromoCategories([]);
      setPromoGroups([]);
      setCategory("");
      setPromoGroup("");
      return;
    }
    api.get("/categories", { params: { promo: true } })
      .then((r) => setPromoCategories(Array.isArray(r.data) ? r.data.sort() : []))
      .catch(() => setPromoCategories([]));
    api.get("/promo-groups")
      .then((r) => setPromoGroups(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPromoGroups([]));
  }, [promoOnly]);

  // Monotonic request counter — guards against out-of-order responses.
  // Broad queries (short SKU prefixes) are slower on 500k rows than precise
  // ones, so a stale response could arrive AFTER the newest one and overwrite
  // the correct results ("results flash then disappear" bug).
  const reqSeq = useRef(0);

  useEffect(() => {
    setLoading(true);
    const seq = ++reqSeq.current;
    // When the customer is searching, ignore the current brand filter — they
    // should see hits across ALL brands, not just the one they happen to be on.
    // The brand_group filter is still honoured during search so users can scope
    // search to e.g. only Mercedes parts when in the Mercedes group.
    const effectiveBrand = qDebounced ? undefined : (brand || undefined);
    api.get("/products", {
      params: {
        q: qDebounced || undefined,
        brand: effectiveBrand,
        brand_group: brandGroup || undefined,
        promo: promoOnly || undefined,
        promo_label: (promoOnly && promoGroup) ? promoGroup : undefined,
        category: category || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      },
    })
      .then((r) => {
        if (seq !== reqSeq.current) return; // stale response — drop it
        setProducts(r.data.items);
        setTotal(r.data.total);
      })
      .finally(() => {
        if (seq === reqSeq.current) setLoading(false);
      });
  }, [qDebounced, brand, brandGroup, promoOnly, category, promoGroup, page]);

  // Sync URL with brand selection
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (brand) next.set("brand", brand); else next.delete("brand");
    if (brandGroup) next.set("brand_group", brandGroup); else next.delete("brand_group");
    if (qDebounced) next.set("q", qDebounced); else next.delete("q");
    if (promoOnly) next.set("promo", "true"); else next.delete("promo");
    if (category) next.set("category", category); else next.delete("category");
    if (promoOnly && promoGroup) next.set("promo_label", promoGroup); else next.delete("promo_label");
    setParams(next, { replace: true });
    // eslint-disable-next-line
  }, [brand, brandGroup, qDebounced, promoOnly, category, promoGroup]);

  // Reflect URL → state when navigating from another page (e.g. Akciók nav link)
  useEffect(() => {
    const p = params.get("promo") === "true";
    if (p !== promoOnly) setPromoOnly(p);
    // eslint-disable-next-line
  }, [params.get("promo")]);

  // Same for brand / brand_group — the navbar links (?brand=piacter, ?brand=bmw...)
  // are in-app <Link>s so Catalog never remounts; without this sync the click
  // changes the URL but the filter state (and thus the product list) stays stale.
  useEffect(() => {
    const pb = params.get("brand") || "";
    if (pb !== brand) setBrand(pb);
    const pbg = params.get("brand_group") || "";
    if (pbg !== brandGroup) setBrandGroup(pbg);
    // eslint-disable-next-line
  }, [params.get("brand"), params.get("brand_group")]);

  const myGroup = useMemo(() => groups.find((g) => g.id === user?.price_group_id), [groups, user]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isSearching = !!qDebounced;
  const BRAND_GROUP_LABELS = {
    vag: "VW Gruppe",
    mercedes: "Mercedes",
    bmw: "BMW",
  };
  const brandLabel = isSearching
    ? "Keresési találatok minden márkából"
    : promoOnly
      ? "Akciós ajánlatok"
      : brand === "piacter"
        ? "Akciós Piactér"
        : brandGroup && BRAND_GROUP_LABELS[brandGroup]
          ? BRAND_GROUP_LABELS[brandGroup] + " alkatrészek"
          : (brand && BRANDS[brand] ? BRANDS[brand].label : "Összes alkatrész");

  // Which sidebar brand sub-buttons to show — VW Gruppe = audi/vw/skoda/seat,
  // Mercedes group renders ONLY the PKW / LKW sub-brands (legacy combined
  // `mercedes` is hidden — its products migrated into PKW/LKW), BMW = bmw.
  const VAG_KEYS = ["audi", "vw", "skoda", "seat"];
  const MERCEDES_KEYS = ["mercedes-pkw", "mercedes-lkw"];
  const visibleBrandKeys = brandGroup === "vag"
    ? VAG_KEYS.filter((k) => BRANDS[k])
    : brandGroup === "mercedes"
      ? MERCEDES_KEYS.filter((k) => BRANDS[k])
      : brandGroup === "bmw"
        ? Object.keys(BRANDS).filter((k) => k === "bmw")
        : Object.keys(BRANDS).filter((k) => k !== "mercedes-pkw" && k !== "mercedes-lkw" && k !== "mercedes");

  return (
    <div>
      <Navbar />

      {/* Hero banner with car background */}
      <div
        className="relative border-b"
        style={{
          borderColor: "var(--border)",
          backgroundImage: `linear-gradient(rgba(255,255,255,0.55), rgba(245,245,245,0.85)), url("https://static.prod-images.emergentagent.com/jobs/3f35fb77-a00a-4f10-8184-cec3e1e072c5/images/642600382e028690741e8eb2801b25815bbad9f0328b1bbeb7f38ce46fbff615.png")`,
          backgroundSize: "cover",
          backgroundPosition: "center 35%",
        }}
        data-testid="catalog-hero"
      >
        <div className="max-w-[1200px] mx-auto px-4 py-10 sm:py-14">
          <div className="max-w-[640px]">
            <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: "var(--brand)" }}>
              {t("catalog_hero_eyebrow", "VAG SERVICE — OEM Nagykereskedés")}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold uppercase tracking-tight mb-3 leading-tight" style={{ color: "var(--text)" }}>
              {t("catalog_hero_title", "Eredeti Audi · VW · Škoda · Seat alkatrészek")}
              <span style={{ color: "var(--brand)" }}>.</span>
            </h2>
            <p className="text-sm sm:text-base mb-4" style={{ color: "var(--text-mute)" }}>
              {t("catalog_hero_subtitle", "369 000+ cikkszám · Szerződéses árazás")}
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs font-bold uppercase px-3 py-1.5 rounded" style={{ background: "#fff", color: "var(--out-stock)", border: "1px solid var(--out-stock)" }}>
                {t("catalog_hero_notice", "Az árak NETTÓ árak — ÁFA-t nem tartalmaznak")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="max-w-[1200px] mx-auto px-4">
          <Link to="/catalog">Kezdőlap</Link> / <span style={{ color: "var(--text)" }}>{brandLabel}</span>
        </div>
      </div>

      <main className="max-w-[1200px] mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-[220px_1fr] gap-5">
          {/* Sidebar */}
          <aside className="space-y-4">
            <div className="card-elev overflow-hidden">
              <div className="section-title">Márkacsoport</div>
              <div className="bg-white">
                {[
                  { key: "", label: "Mind" },
                  { key: "vag", label: "VW Gruppe (A/VW/Š/S)" },
                  { key: "mercedes", label: "Mercedes" },
                  { key: "bmw", label: "BMW" },
                ].map((g) => {
                  const active = brandGroup === g.key;
                  return (
                    <button
                      key={g.key || "all"}
                      onClick={() => {
                        setBrandGroup(g.key);
                        setBrand("");
                        setPromoOnly(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm border-b ${active ? "font-bold" : ""}`}
                      style={{
                        borderColor: "var(--border)",
                        background: active ? "var(--surface-elev)" : "transparent",
                        color: active ? "var(--brand)" : "var(--text)",
                      }}
                      data-testid={`brand-group-${g.key || "all"}`}
                    >
                      {g.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="card-elev overflow-hidden">
              <div className="section-title">Márkák</div>
              <div className="bg-white">
                <button
                  onClick={() => { setBrand(""); setPromoOnly(false); }}
                  className={`w-full text-left px-3 py-2 text-sm border-b ${!brand && !promoOnly ? "font-bold" : ""}`}
                  style={{ borderColor: "var(--border)", background: !brand && !promoOnly ? "var(--surface-elev)" : "transparent", color: !brand && !promoOnly ? "var(--brand)" : "var(--text)" }}
                  data-testid="brand-all"
                >
                  Összes alkatrész
                </button>
                <button
                  onClick={() => { setPromoOnly(!promoOnly); setBrand(""); }}
                  className={`w-full text-left px-3 py-2 text-sm border-b flex items-center gap-2 ${promoOnly ? "font-bold" : ""}`}
                  style={{ borderColor: "var(--border)", background: promoOnly ? "rgba(200,16,46,0.08)" : "transparent", color: promoOnly ? "#c8102e" : "#c8102e" }}
                  data-testid="brand-promo"
                >
                  <Flame size={14} />
                  <span className="flex-1">Akciók</span>
                </button>
                {visibleBrandKeys.map((key) => {
                  const b = BRANDS[key];
                  if (!b) return null;
                  return (
                    <button
                      key={key}
                      onClick={() => { setBrand(brand === key ? "" : key); setPromoOnly(false); }}
                      className={`w-full text-left px-3 py-2 text-sm border-b flex items-center gap-2 ${brand === key ? "font-bold" : ""}`}
                      style={{ borderColor: "var(--border)", background: brand === key ? "var(--surface-elev)" : "transparent", color: brand === key ? "var(--brand)" : "var(--text)" }}
                      data-testid={`brand-${key}`}
                    >
                      <img src={darkLogoFor(b)} alt="" className="h-3.5 w-3.5 object-contain" />
                      <span className="flex-1">{b.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {promoOnly && promoGroups.length > 0 && (
              <div className="card-elev overflow-hidden" data-testid="promo-groups-card">
                <div className="section-title flex items-center gap-2" style={{ background: "linear-gradient(180deg, #c8102e 0%, #9b0d24 100%)" }}>
                  <Flame size={12} /> Akció termékcsoportok
                </div>
                <div className="bg-white">
                  <button
                    onClick={() => setPromoGroup("")}
                    className={`w-full text-left px-3 py-2 text-sm border-b ${!promoGroup ? "font-bold" : ""}`}
                    style={{
                      borderColor: "var(--border)",
                      background: !promoGroup ? "rgba(200,16,46,0.08)" : "transparent",
                      color: !promoGroup ? "#c8102e" : "var(--text)",
                    }}
                    data-testid="promo-group-all"
                  >
                    Összes csoport
                  </button>
                  {promoGroups.map((g) => {
                    const active = promoGroup === g.label;
                    return (
                      <button
                        key={g.label}
                        onClick={() => setPromoGroup(active ? "" : g.label)}
                        className={`w-full text-left px-3 py-2 text-sm border-b flex items-center justify-between gap-2 ${active ? "font-bold" : ""}`}
                        style={{
                          borderColor: "var(--border)",
                          background: active ? "rgba(200,16,46,0.08)" : "transparent",
                          color: active ? "#c8102e" : "var(--text)",
                        }}
                        data-testid={`promo-group-${g.label}`}
                        title={g.label}
                      >
                        <span className="flex-1 truncate">{g.label}</span>
                        <span className="text-[10px]" style={{ color: "var(--text-mute)" }}>({g.count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {promoOnly && promoCategories.length > 0 && (
              <div className="card-elev overflow-hidden" data-testid="promo-categories-card">
                <div className="section-title flex items-center gap-2" style={{ background: "linear-gradient(180deg, #c8102e 0%, #9b0d24 100%)" }}>
                  <Flame size={12} /> Akciók kategóriánként
                </div>
                <div className="bg-white">
                  <button
                    onClick={() => setCategory("")}
                    className={`w-full text-left px-3 py-2 text-sm border-b ${!category ? "font-bold" : ""}`}
                    style={{
                      borderColor: "var(--border)",
                      background: !category ? "rgba(200,16,46,0.08)" : "transparent",
                      color: !category ? "#c8102e" : "var(--text)",
                    }}
                    data-testid="promo-cat-all"
                  >
                    Összes akció
                  </button>
                  {promoCategories.map((c) => {
                    const active = category === c;
                    return (
                      <button
                        key={c}
                        onClick={() => setCategory(active ? "" : c)}
                        className={`w-full text-left px-3 py-2 text-sm border-b ${active ? "font-bold" : ""}`}
                        style={{
                          borderColor: "var(--border)",
                          background: active ? "rgba(200,16,46,0.08)" : "transparent",
                          color: active ? "#c8102e" : "var(--text)",
                        }}
                        data-testid={`promo-cat-${c}`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {myGroup && (
              <div className="card-elev overflow-hidden">
                <div className="section-title">Az Ön szintje</div>
                <div className="bg-white p-3 text-sm">
                  <div className="font-bold text-base">{myGroup.name}</div>
                  {myGroup.discount_percent > 0 && (
                    <div className="text-xs mt-1" style={{ color: "var(--in-stock)" }}>−{myGroup.discount_percent}% szerződéses kedvezmény</div>
                  )}
                </div>
              </div>
            )}

            <div className="card-elev overflow-hidden">
              <div className="section-title">Készlet jelmagyarázat</div>
              <div className="bg-white p-3 space-y-1.5 text-xs">
                <div className="flex items-center gap-2"><span className="stock-badge stock-in" style={{ width: 10, height: 10, padding: 0 }} />Raktáron · azonnal szállítható</div>
                <div className="flex items-center gap-2"><span className="stock-badge stock-low" style={{ width: 10, height: 10, padding: 0 }} />Rendelhető · beszerzés 2–3 nap</div>
              </div>
            </div>
          </aside>

          {/* Main */}
          <section>
            <h1 className="text-2xl font-bold mb-2 uppercase">{brandLabel}</h1>
            <div className="border-b-2 mb-4" style={{ borderColor: "var(--brand)" }} />

            <div
              className="mb-4 p-2 flex items-center gap-2"
              style={{
                background: "#FFFEF0",
                border: "2px solid var(--brand)",
                borderRadius: 4,
                boxShadow: "0 1px 4px rgba(38,128,194,0.12)",
              }}
            >
              <Search size={14} style={{ color: "var(--brand)" }} />
              <input
                className="!py-1.5 !px-1 flex-1 font-bold text-sm placeholder:font-bold placeholder:text-[color:var(--text-mute)]"
                style={{ background: "transparent", border: "none", outline: "none", color: "var(--text)" }}
                placeholder="Cikkszám..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setQDebounced(q); }}
                autoFocus
                data-testid="catalog-search-input"
              />
              {q && (
                <button onClick={() => setQ("")} className="p-1 hover:opacity-70" style={{ color: "var(--text-mute)" }} title="Törlés">
                  <X size={14} />
                </button>
              )}
              <button
                onClick={() => setQDebounced(q)}
                className="btn-primary !py-1 !px-3 text-xs font-bold uppercase"
                data-testid="catalog-search-btn"
              >
                <Search size={12} /> Keresés
              </button>
              <span className="text-[11px] whitespace-nowrap font-bold pl-2 border-l" style={{ color: "var(--text-mute)", borderColor: "var(--border)" }}>
                <strong style={{ color: "var(--brand)" }}>{total.toLocaleString("hu-HU")}</strong> tétel
              </span>
            </div>

            {loading ? (
              <div className="card-elev p-8 text-center text-sm" style={{ color: "var(--text-mute)" }}>Termékek betöltése...</div>
            ) : products.length === 0 ? (
              <div className="card-elev p-12 text-center" data-testid="empty-catalog">
                <Package size={40} className="mx-auto mb-3" style={{ color: "var(--text-disabled)" }} />
                <h3 className="font-bold text-lg">Nincs találat</h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-mute)" }}>
                  {q ? "Próbáljon másik cikkszámot vagy nevet." : "Az adminok CSV-vel tölthetnek fel adatot."}
                </p>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="product-grid">
                  {products.map((p) => (
                    <ProductCard key={p.id} p={p} onAdd={(qty) => { add(p, qty); toast.success(`${p.sku} × ${qty} kosárba téve`); }} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="card-flat p-3 mt-4 flex items-center justify-between" data-testid="pagination">
                    <div className="text-xs" style={{ color: "var(--text-mute)" }}>
                      <strong>{page + 1}</strong>. oldal / {totalPages.toLocaleString("hu-HU")} · összesen {total.toLocaleString("hu-HU")} tétel
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-ghost !py-1 !px-2 text-[11px] disabled:opacity-30" data-testid="page-prev">
                        <ChevronLeft size={12} /> Előző
                      </button>
                      <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="btn-primary !py-1 !px-2 text-[11px] disabled:opacity-30" data-testid="page-next">
                        Következő <ChevronRight size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function ProductCard({ p, onAdd }) {
  const [qty, setQty] = useState(1);
  const noPrice = !p.your_price || p.your_price === 0;
  const brand = brandFor(p);

  // Stock + availability badge.
  // Promo (akciós) items are always shown as immediately available — the
  // supplier guarantees stock on promo SKUs regardless of our live ETS count.
  let stockClass, stockLabel;
  if (p.on_promo) {
    stockClass = "stock-in";
    stockLabel = "Azonnal elérhető";
  } else if (p.stock <= 0) {
    stockClass = "stock-low";
    stockLabel = "Rendelhető · 2–3 nap";
  } else if (p.available_immediate) {
    stockClass = "stock-in";
    stockLabel = `Raktáron · ${p.stock} db`;
  } else if (p.stock < 5) {
    stockClass = "stock-low";
    stockLabel = `Beszerzés 2–3 nap · ${p.stock} db`;
  } else {
    stockClass = "stock-low";
    stockLabel = "Beszerzés 2–3 nap";
  }

  // Promo: red price + strikethrough on "list" (markup-applied non-promo) price
  const onPromo = !noPrice && p.on_promo && p.your_list_price && p.your_list_price > p.your_price;
  const discountPct = onPromo ? Math.round((1 - p.your_price / p.your_list_price) * 100) : 0;

  return (
    <article
      className="card-elev flex flex-col group transition-shadow hover:shadow-md"
      style={onPromo ? { boxShadow: "0 0 0 2px #c8102e, 0 2px 8px rgba(200,16,46,0.18)" } : undefined}
      data-testid={`product-card-${p.sku}`}
    >
      <Link to={`/product/${p.id}`} className="block">
        <div
          className="relative p-5 flex flex-col items-center justify-center bg-white border-b min-h-[120px]"
          style={{ borderColor: "var(--border)" }}
        >
          {brand && (
            <div
              className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded"
              style={{ background: "var(--surface-elev)", border: "1px solid var(--border)" }}
            >
              <img
                src={darkLogoFor(brand)}
                alt={brand.label}
                className="h-3 w-3 object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "var(--text-mute)" }}>
                {brand.label}
              </span>
            </div>
          )}
          <div className={`absolute top-2 right-2 stock-badge ${stockClass}`}>{stockLabel}</div>

          {/* Promo ribbon */}
          {onPromo && (
            <div
              className="absolute -left-1 top-9 flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
              style={{ background: "#c8102e", color: "#fff", boxShadow: "0 2px 4px rgba(0,0,0,0.15)" }}
              data-testid={`promo-ribbon-${p.sku}`}
            >
              <Flame size={10} /> Akció −{discountPct}%
            </div>
          )}

          {/* Marketplace photo (if any) — otherwise SKU dominant */}
          {p.image_url ? (
            <>
              <img
                src={p.image_url}
                alt={p.name}
                className="max-h-[110px] max-w-full object-contain mt-3"
                loading="lazy"
                style={p.sold ? { filter: "grayscale(0.8)", opacity: 0.7 } : undefined}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div
                className="font-mono font-bold text-sm text-center break-all px-2 mt-2"
                style={{ color: onPromo ? "#c8102e" : "var(--brand)", letterSpacing: "0.02em" }}
              >
                {p.sku}
              </div>
            </>
          ) : (
            <div
              className="font-mono font-bold text-xl sm:text-2xl text-center break-all px-2 mt-3"
              style={{ color: onPromo ? "#c8102e" : "var(--brand)", letterSpacing: "0.02em" }}
            >
              {p.sku}
            </div>
          )}

          {/* Sold overlay (marketplace) */}
          {p.sold && (
            <div
              className="absolute inset-0 flex items-center justify-center"
              data-testid={`sold-overlay-${p.sku}`}
            >
              <span
                className="px-4 py-1.5 text-sm font-bold uppercase tracking-widest rotate-[-8deg]"
                style={{ background: "#c8102e", color: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}
              >
                Eladva
              </span>
            </div>
          )}
        </div>
      </Link>
      <div className="p-3 flex-1 flex flex-col">
        <Link to={`/product/${p.id}`} className="block hover:underline" style={{ color: "var(--text)" }}>
          <h3 className="text-xs leading-snug line-clamp-2 min-h-[2.2em]" style={{ color: "var(--text-mute)" }}>{p.name}</h3>
        </Link>
        <div className="mt-3 pt-2 flex items-end justify-between gap-2 border-t" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="text-[10px] uppercase font-bold" style={{ color: "var(--text-mute)" }}>
              {onPromo ? <span style={{ color: "#c8102e" }}>Akciós nettó</span> : "Nettó"}
            </div>
            {noPrice ? (
              <div className="text-sm font-bold" style={{ color: "var(--text-mute)" }}>Ajánlat</div>
            ) : onPromo ? (
              <div className="leading-tight">
                <div className="text-lg font-bold" style={{ color: "#c8102e" }}>{formatMoney(p.your_price, p.native_price_huf)}</div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[10px] line-through" style={{ color: "var(--text-disabled)" }}>{formatMoney(p.your_list_price)}</span>
                  <span className="text-[10px] font-bold" style={{ color: "#c8102e" }}>−{discountPct}%</span>
                </div>
              </div>
            ) : (
              <div className="text-lg font-bold" style={{ color: "var(--brand)" }}>{formatMoney(p.your_price, p.native_price_huf)}</div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="flex items-stretch border rounded overflow-hidden bg-white"
              style={{ borderColor: "var(--border)" }}
              data-testid={`qty-stepper-${p.sku}`}
            >
              <button
                type="button"
                onClick={() => setQty((n) => Math.max(1, n - 1))}
                disabled={qty <= 1}
                className="px-1.5 text-sm font-bold disabled:opacity-30 hover:bg-slate-100"
                style={{ color: "var(--text-mute)" }}
                aria-label="Csökkentés"
                data-testid={`qty-dec-${p.sku}`}
              >
                −
              </button>
              <input
                type="number"
                min={1}
                max={9999}
                value={qty}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setQty(Number.isFinite(v) && v > 0 ? Math.min(9999, v) : 1);
                }}
                className="w-9 text-center text-[11px] font-bold border-x outline-none qty-input-bare"
                style={{ borderColor: "var(--border)", color: "var(--text)" }}
                aria-label="Darabszám"
                data-testid={`qty-input-${p.sku}`}
              />
              <button
                type="button"
                onClick={() => setQty((n) => Math.min(9999, n + 1))}
                className="px-1.5 text-sm font-bold hover:bg-slate-100"
                style={{ color: "var(--text-mute)" }}
                aria-label="Növelés"
                data-testid={`qty-inc-${p.sku}`}
              >
                +
              </button>
            </div>
            <button
              onClick={() => onAdd(qty)}
              disabled={noPrice || p.sold}
              className="btn-primary !py-2 !px-3.5 text-sm font-bold uppercase tracking-wide"
              data-testid={`add-to-cart-${p.sku}`}
              title={p.sold ? "Ez a termék már elkelt" : noPrice ? "Nincs ár megadva" : `${qty} db kosárba`}
            >
              <ShoppingCart size={15} /> {p.sold ? "Eladva" : "Kosárba"}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
